import React, { useEffect, useState, useRef, Fragment } from 'react';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../ui/LatexRenderer';
/**
 * NumberSequenceRenderer - Fixed version with proper decimal support
 *
 * FIXES:
 * - Changed from type="number" to type="text" with inputMode="decimal" for mobile
 * - Proper handling of decimal numbers (0.1, 0.2, 0.3, etc.)
 * - Better mobile keyboard support
 * - Improved validation and error states
 * - Better accessibility
 */
export function NumberSequenceRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  // Read from question.data (canonical) with fallback to interactiveConfig (legacy)
  const config = question.data || question.interactiveConfig || {};
  const sequence = config.sequence || []; // [3, 7, 11, 15] or ["€1,00", "€1,50", null, "€2,50"]
  const missingIndices = config.missingIndices || []; // [4] (index where input should be)
  // Auto-detect missing indices from null values in sequence if missingIndices not provided
  const effectiveMissingIndices = missingIndices.length > 0 ? missingIndices : sequence.reduce((acc: number[], val: any, idx: number) => {
    if (val === null || val === undefined) acc.push(idx);
    return acc;
  }, []);
  const prevValidateRef = useRef(false);
  // Map of index -> value
  const [inputs, setInputs] = useState<Record<number, string>>({});
  // Reset state when question changes
  useEffect(() => {
    setInputs({});
    prevValidateRef.current = false;
  }, [question.id]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  useEffect(() => {
    // Ready when all missing indices have a value
    const isReady = effectiveMissingIndices.every((idx: number) => inputs[idx] && inputs[idx].trim() !== '');
    onReadyChange?.(isReady);
  }, [inputs, effectiveMissingIndices, onReadyChange]);
  // Rising-edge detection for shouldValidate
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      // Return comma-separated values in order of effectiveMissingIndices
      const values = effectiveMissingIndices.map((idx: number) => inputs[idx] || '').join(',');
      onAnswer(values);
    }
  }, [shouldValidate, inputs, effectiveMissingIndices, onAnswer]);
  const handleChange = (index: number, value: string) => {
    setInputs(prev => ({
      ...prev,
      [index]: value
    }));
  };
  // Determine the full length to render
  const maxMissing = effectiveMissingIndices.length > 0 ? Math.max(...effectiveMissingIndices) : -1;
  const maxIndex = Math.max(sequence.length - 1, maxMissing) + 1;
  // Create array of items to render
  const items = [];
  for (let i = 0; i < maxIndex; i++) {
    if (effectiveMissingIndices.includes(i)) {
      items.push({
        type: 'input',
        index: i
      });
    } else if (sequence[i] !== undefined && sequence[i] !== null) {
      items.push({
        type: 'static',
        value: sequence[i]
      });
    }
  }
  // Detect if sequence contains money/time values (has € or :)
  const sampleValue = sequence.find((v: any) => v !== null && v !== undefined);
  const isMoneyOrTime = sampleValue && (String(sampleValue).includes('€') || String(sampleValue).includes(':'));
  return <div className="w-full max-w-4xl mx-auto" data-id="element-4814">
      <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4" data-id="element-4815">
        {items.map((item, i) => <Fragment key={i} data-id="element-4816">
            {item.type === 'static' ? <div className="min-w-[80px] md:min-w-[100px] h-16 md:h-20 flex items-center justify-center bg-white border-2 border-slate-200 rounded-2xl text-lg md:text-xl font-bold text-slate-700 shadow-sm px-3" data-id="element-4817">
                <LatexRenderer content={String(item.value)} data-id="element-4818" />
              </div> : <input type="text" inputMode={isMoneyOrTime ? 'text' : 'decimal'} value={inputs[item.index] || ''} onChange={e => handleChange(item.index, e.target.value)} className="min-w-[80px] md:min-w-[100px] h-16 md:h-20 text-center bg-white border-2 border-indigo-300 rounded-2xl text-lg md:text-xl font-bold text-indigo-700 shadow-sm focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:text-indigo-200 px-2" placeholder="?" aria-label={`Ontbrekend getal ${i + 1}`} data-id="element-4819" />}

            {/* Arrow between items */}
            {i < items.length - 1 && <div className="text-slate-300 flex-shrink-0" data-id="element-4820">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 md:w-6 md:h-6" data-id="element-4821">
                  <line x1="5" y1="12" x2="19" y2="12" data-id="element-4822"></line>
                  <polyline points="12 5 19 12 12 19" data-id="element-4823"></polyline>
                </svg>
              </div>}
          </Fragment>)}
      </div>

      <p className="text-center text-slate-400 text-sm mt-6 md:mt-8 font-medium" data-id="element-4824">
        Vul de ontbrekende getallen in
      </p>
    </div>;
}